import { useState } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import { parseAsBoolean, useQueryState } from "nuqs";

import { useGetAllManagers } from "@/entities/manager";
import { useURLParamsData } from "@/shared/hooks/useURLParamsData";
import { Roles } from "@/entities/user";
import { DataTable } from "@/shared/ui/DataTable";
import { WorkersColumns } from "@/widgets/tables";
import { ManagerColumns } from "@/widgets/tables/teamlead";
import { useModifyDataForTable } from "@/shared/hooks/useModifyDataForTable";
import { TableSearchInput } from "@/features/TableSearchInput/TableSearchInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { localizationMap } from "@/shared/lib/consts";
import { useTeamleads } from "@/entities/teamlead/api";
import { TeamleadsColumns } from "./TeamleadColumns";
import { useGetModerators } from "@/entities/moderator/api/api";
import { ModeratorsColumns } from "@/pages/restricted/head/moderators-page/model/columns";
import { Button } from "@/shared/ui/button";
import { useWorkers } from "@/entities/worker";

export const HeadDashboardProDataTable = <T,>() => {
  const { page, search, setPage, sort, length, status, order } =
    useURLParamsData();
  const [isFree] = useQueryState("isFree", parseAsBoolean);
  const [selectedRole, setSelectedRole] = useState<Roles>(Roles.Worker);

  const { data, isPending, refetch } = (() => {
    switch (selectedRole) {
      case Roles.Worker:
        return useWorkers({
          pageSize: 10,
          search,
          currentPage: page,
          sort: sort as "createdAt" | null,
          order,
          isFree,
          status,
        });
      case Roles.Manager:
        return useGetAllManagers({
          pageSize: length,
          currentPage: page,
          search,
          sort,
          order,
        });
      case Roles.TeamLead:
        return useTeamleads({
          pageSize: length,
          currentPage: page,
          search,
          sort,
          order: order || undefined,
          status,
          isMaster: false,
        });
      case Roles.MasterTeamLead:
        return useTeamleads({
          pageSize: length,
          currentPage: page,
          search,
          sort,
          order: order || undefined,
          status,
          isMaster: true,
        });
      case Roles.MasterModerator:
        return useGetModerators({
          pageSize: length,
          currentPage: page,
          search: search,
          isMaster: true,
        });
      case Roles.Moderator:
        return useGetModerators({
          pageSize: length,
          currentPage: page,
          search: search,
          isMaster: false,
        });
      default:
        return { data: null, isPending: false, refetch: () => {} };
    }
  })();

  //TODO: тоже позор

  const getColumnsConfig = (): ColumnDef<T>[] => {
    switch (selectedRole) {
      case Roles.Manager:
        return ManagerColumns as ColumnDef<T>[];
      case Roles.Worker:
        return WorkersColumns as ColumnDef<T>[];
      case Roles.MasterTeamLead:
      case Roles.TeamLead:
        return TeamleadsColumns as ColumnDef<T>[];
      case Roles.MasterModerator:
      case Roles.Moderator:
        return ModeratorsColumns as ColumnDef<T>[];
      default:
        return [];
    }
  };

  const columns = getColumnsConfig() as any[];

  const getModifiedData = () => {
    const workerMutations = {
      login: (item: any) => item.username,
    };

    let roleSpecificMutations: Record<string, (item: any) => any> = {};

    switch (selectedRole) {
      case Roles.Worker:
        roleSpecificMutations = workerMutations;
        break;
      default:
        roleSpecificMutations = {};
    }

    const mutations = {
      ...roleSpecificMutations,
    };

    return useModifyDataForTable<any, any>(data!, mutations);
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <TableSearchInput />
        <SelectRoleDropdown
          rolesArr={[...Object.values(Roles)]}
          setRole={setSelectedRole}
        />
      </div>

      <DataTable<T, unknown>
        data={getModifiedData() as T[]}
        page={page}
        setPage={setPage}
        pageCount={data ? Math.ceil(data.total / length) : 0}
        columns={columns}
        isLoading={isPending}
        selectMobileColumn={selectedRole === Roles.Worker ? 1 : 0}
        refetch={refetch}
      />
    </div>
  );
};

type SelectRoleDropdownProps<T extends Roles> = {
  rolesArr: readonly T[];
  setRole: (role: T) => void;
};

export const SelectRoleDropdown = <T extends Roles>({
  rolesArr,
  setRole,
}: SelectRoleDropdownProps<T>) => {
  const handleChange = (value: string) => {
    if (rolesArr.includes(value as T)) {
      setRole(value as T);
    }
  };

  return (
    <Select onValueChange={handleChange} defaultValue={rolesArr[0]}>
      <SelectTrigger className="h-10 w-fit">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {rolesArr
          .filter((r) => r !== Roles.Head)
          .map((role) => (
            <SelectItem value={role} key={role}>
              {localizationMap[role]}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
};

interface HandleSelectDPUserButtonProps {
  userId: string;
  userName: string;
  userRole: Roles;
}

export const HandleSelectDPUserButton = ({
  userId,
  userRole,
  userName,
}: HandleSelectDPUserButtonProps) => {
  const { setSelectedUser } = useURLParamsData();

  return (
    <Button
      variant="link"
      className="text-blue-500"
      onClick={() => setSelectedUser(userId, userRole, userName)}
    >
      {userName || "N/A"}
    </Button>
  );
};
